// src/features/groups/components/GroupEdit.tsx

import {
  Edit,
  SimpleForm,
  TextInput,
  required,
  minLength,
  maxLength,
  useTranslate,
  useNotify,
  useRedirect,
  useRecordContext,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  GroupWork as GroupIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { MyFormContainer } from '@/components/forms/MyFormContainer';
import { RegistryLinkInput } from '@/components/forms/RegistryLinkInput';
import { MyFormLayout, FormCard, MyFormSection } from '@/components/forms/MyFormLayout';
import { MyToolbar } from '@/components/forms/MyToolbar';
import { useGroupPermissions } from '../hooks/useGroupPermissions';

/**
 * Copy ID button for the read-only ID field
 */
const CopyIdButton = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Tooltip title="Copiar ID">
      <IconButton
        size="small"
        onClick={() => navigator.clipboard.writeText(String(record.id))}
        sx={{ p: 0.5 }}
      >
        <CopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Validation rules for group editing
 */
const validateGroupName = [
  required('resources.groups.validation.name_required'),
  minLength(3, 'resources.groups.validation.name_min_length'),
  maxLength(100, 'resources.groups.validation.name_max_length'),
];

const validateDescription = [
  maxLength(500, 'resources.groups.validation.description_max_length'),
];

/**
 * Group edit component
 * Only SuperAdmin can access this
 */
export const GroupEdit = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const navigate = useNavigate();
  const { canDelete } = useGroupPermissions();

  const handleCancel = () => navigate('/admin-options?tab=groups');

  const onSuccess = () => {
    notify('resources.groups.messages.update_success', { type: 'success' });
    redirect('/admin-options?tab=groups');
  };

  const onError = (error: any) => {
    const errorMessage = error?.body?.error?.message || 
                        error?.message || 
                        translate('resources.groups.messages.update_error');
    notify(errorMessage, { type: 'error' });
  };

  return (
    <Edit
      mutationOptions={{ onSuccess, onError }}
      mutationMode="pessimistic"
      title={translate('resources.groups.edit.title')}
    >
      <SimpleForm
        toolbar={
          <MyToolbar
            resource="groups"
            showDelete={canDelete}
            deleteButton={canDelete}
            onCancel={handleCancel}
          />
        }
      >
        <MyFormContainer maxWidth="lg">
          <MyFormLayout centered={false} maxColumns={1}>
           
            {/* Basic Information */}
            <FormCard xs={12}>
              <MyFormSection
                title={translate('resources.groups.edit.title')}
                subtitle={translate('resources.groups.edit.subtitle')}
                icon={<GroupIcon />}
                variant="clean"
              >
                {/* Group ID - read-only, for Keycloak mapping */}
                <TextInput
                  source="id"
                  label="ID"
                  disabled
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <CopyIdButton />
                    ),
                    sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                  }}
                  sx={{ mb: 1 }}
                  helperText="ID interno del grupo"
                />

                {/* Group Name */}
                <TextInput
                  source="name"
                  label={translate('resources.groups.fields.name')}
                  validate={validateGroupName}
                  fullWidth
                  helperText={translate('resources.groups.validation.name_min_length')}
                />

                {/* Description */}
                <TextInput
                  source="description"
                  label={translate('resources.groups.fields.description')}
                  validate={validateDescription}
                  fullWidth
                  multiline
                  rows={4}
                  helperText={translate('resources.groups.validation.description_max_length')}
                />

                {/* Record of the shared registry */}
                <RegistryLinkInput
                  source="directoryId"
                  kind="organization"
                  label={translate('resources.groups.fields.directoryId')}
                />
              </MyFormSection>
            </FormCard>

          </MyFormLayout>
        </MyFormContainer>
      </SimpleForm>
    </Edit>
  );
};